/**
 * Send the customer their onboarding form, to whoever should fill it in.
 *
 * It used to go to the champion and nobody else, from a button on Contacts
 * that also misread the reply and said "Nothing was emailed" when it had been.
 * Here it says who it last went to, when, and until when the link works, and
 * it can go again to anybody recorded or to an address typed in. Sending again
 * cancels the earlier link, so only one is ever live.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { call, isCallFailure } from '../../../lib/callable';
import { formatDayUS } from '../../../shared/dates';
import { EMAIL_MAX } from '../../../types/fieldLimits';
import page from '../../../shared/Page.module.css';
import form from '../../../shared/Form.module.css';
import { ROLE_LABEL } from './messageFor';
import type { Onboarding } from './types';
import { primaryFor } from './people';
import { GroupCard } from '../../../shared/GroupCard';

const OTHER = '__other__';

interface SendResult {
  url: string;
  sentTo: string | null;
  emailSent: boolean;
  emailReason: string | null;
  expiresAt: string;
}

export function SendIntakeForm({ data }: { data: Onboarding }) {
  const queryClient = useQueryClient();
  const last = data.intakeLink ?? null;
  const champion = primaryFor(data.people, 'champion');
  const initial = data.people.find((p) => p.email === last?.sentTo)?.email
    ?? champion?.email ?? data.people[0]?.email ?? OTHER;

  const [choice, setChoice] = useState(initial);
  const [other, setOther] = useState(last?.sentTo && !data.people.some((p) => p.email === last.sentTo) ? last.sentTo : '');
  const [result, setResult] = useState<SendResult | null>(null);

  const to = choice === OTHER ? other.trim().toLowerCase() : choice;
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to);

  const send = useMutation({
    mutationFn: () => call<{ orgId: string; email: string }, SendResult>(
      'sysAdminCreateIntakeLink', { orgId: data.orgId, email: to },
    ),
    onSuccess: (r) => {
      setResult(r);
      void queryClient.invalidateQueries({ queryKey: ['onboarding', data.orgId] });
      void queryClient.invalidateQueries({ queryKey: ['onboarding-activity', data.orgId] });
    },
  });

  return (
    <GroupCard id="intake-send-title" title="The onboarding form">
      <p className={page.itemMeta}>
        {!last ? 'Not sent yet. The customer fills in their own details, contacts and paperwork needs.'
          : last.expired
            ? <span className={page.warnText}>
                Sent{last.sentTo ? ` to ${last.sentTo}` : ''}{last.sentAt ? ` on ${formatDayUS(last.sentAt)}` : ''}. The link expired
                {last.expiresAt ? ` on ${formatDayUS(last.expiresAt)}` : ''}.
              </span>
            : <>Sent{last.sentTo ? ` to ${last.sentTo}` : ''}{last.sentAt ? ` on ${formatDayUS(last.sentAt)}` : ''}.
                {last.expiresAt ? ` The link works until ${formatDayUS(last.expiresAt)}.` : ''}</>}
      </p>

      <div className={form.row} style={{ marginTop: 'var(--sp-3)', alignItems: 'end' }}>
        <div>
          <label className={form.label} htmlFor="intake-to">Send it to</label>
          <select id="intake-to" className={form.select} value={choice}
            onChange={(e) => { setChoice(e.target.value); setResult(null); }}>
            {data.people.map((p) => (
              <option key={p.id} value={p.email}>
                {p.name} · {p.email} ({p.roles.map((r) => ROLE_LABEL[r] ?? r).join(', ')})
              </option>
            ))}
            <option value={OTHER}>Another address</option>
          </select>
        </div>
        {choice === OTHER && (
          <div>
            <label className={form.label} htmlFor="intake-other">Email address</label>
            <input id="intake-other" type="email" className={form.input} maxLength={EMAIL_MAX}
              placeholder="name@hospital.org" value={other}
              onChange={(e) => { setOther(e.target.value); setResult(null); }} />
          </div>
        )}
      </div>

      <div className={page.actions} style={{ marginTop: 'var(--sp-3)' }}>
        <button type="button" className={`${page.btn} ${page.btnPrimary}`}
          disabled={!valid || send.isPending} onClick={() => send.mutate()}>
          {send.isPending ? 'Sending…' : last ? 'Send it again' : 'Send the form'}
        </button>
      </div>
      {last && !last.expired && (
        <p className={form.hint}>Sending again cancels the link sent earlier, so only the new one works.</p>
      )}

      {send.error && (
        <div className={`${page.notice} ${page.noticeDanger}`} role="alert" style={{ marginTop: 'var(--sp-3)' }}>
          {isCallFailure(send.error) ? send.error.message : 'The form was not sent.'}
        </div>
      )}
      {result && (
        <div className={`${page.notice} ${result.emailSent ? page.noticeInfo : page.noticeWarn}`} role="status" style={{ marginTop: 'var(--sp-3)' }}>
          {result.emailSent
            ? <>Sent to <strong>{result.sentTo}</strong>. The link works until {formatDayUS(result.expiresAt)}.</>
            : <>Nothing was emailed{result.emailReason ? `: ${result.emailReason}` : '.'} Send them this link yourself:{' '}
                <span style={{ fontFamily: 'var(--font-mono)', overflowWrap: 'anywhere' }}>{result.url}</span></>}
        </div>
      )}
    </GroupCard>
  );
}
